import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { BookCover } from "./BookCover";
import { AUTHOR, scrollToId } from "../lib/site";

const LINES = [["Bipolar", ""], ["is who", "italic"], ["I am.", "gold"]];
const ease = [0.77, 0, 0.18, 1];

const MaskLine = ({ text, style, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${style === "italic" ? "italic font-normal pl-[0.6em]" : ""} ${style === "gold" ? "gold-foil" : ""}`}
      initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.15, delay: 0.25 + i * 0.14, ease }}>
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] pt-28 pb-16 overflow-hidden">
      <div className="absolute -top-40 right-[-10%] w-[640px] h-[640px] rounded-full bg-[#5e253b]/40 blur-[140px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p className="overline mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            The reader-supported page of {AUTHOR.name}
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif-d font-semibold tracking-tight leading-[0.9] text-[4.2rem] sm:text-[6.5rem] lg:text-[8.2rem]">
            {LINES.map(([t, s], i) => <MaskLine key={t} text={t} style={s} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-[#c4bbb0] font-light">
            {AUTHOR.blurb} If my writing has kept you company, you can help me keep writing.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-tip-button" onClick={() => scrollToId("tip")}
              className="group rounded-full bg-[#e3ba73] text-[#121016] pl-7 pr-5 py-4 font-semibold flex items-center gap-3 hover:bg-[#f4d79e] transition-colors duration-300">
              Leave a tip
              <span className="grid place-items-center w-8 h-8 rounded-full bg-[#121016] text-[#e3ba73] transition-transform duration-500 group-hover:rotate-[-45deg]">
                <ArrowDownRight size={16} />
              </span>
            </button>
            <button data-testid="hero-story-button" onClick={() => scrollToId("story")}
              className="rounded-full border border-[#f4ede2]/20 px-7 py-4 text-sm hover:border-[#e3ba73] hover:text-[#e3ba73] transition-colors duration-300">
              About the book
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: bookY }} className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.92, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <BookCover className="py-6" />
          <p className="mt-6 text-center font-mono-d text-[11px] tracking-[0.3em] uppercase text-[#c4bbb0]">Move your cursor over the cover</p>
        </motion.div>
      </div>
    </section>
  );
};
